import { API_BASE_URL, ADMIN_API_URL } from '../config/site';
import {
  Project,
  Article,
  LifeMetric,
  SiteControls,
  SiteControlRow,
  SocialChannel,
  AboutData,
} from '../types/content';
import {
  seedProjects,
  seedArticles,
  seedSocialChannels,
  seedLifeMetrics,
  seedAboutData,
  defaultSiteControls,
} from '../data/seed';

const REQUEST_TIMEOUT_MS = 4000;

export const CONTENT_CHANGE_EVENT = 'shvms-content-change';

export const STORAGE_KEYS = {
  PROJECTS: 'shvms_projects_override',
  BLOGS: 'shvms_blogs_override',
  SOCIAL: 'shvms_social_override',
  TELEMETRY: 'shvms_telemetry_override',
  ABOUT: 'shvms_about_override',
  CONTROLS: 'shvms_site_controls',
};

function notifyContentChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONTENT_CHANGE_EVENT));
  }
}

/**
 * Fetch wrapper with timeout and error tolerance.
 * Returns null if network fails or takes longer than timeout.
 */
async function safeFetch<T>(url: string, options: RequestInit = {}): Promise<T | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    return data as T;
  } catch (err) {
    // Graceful fallback on network timeout, CORS, or offline server
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch projects:
 * 1. Client localStorage override (configured via /admin)
 * 2. Remote PostgREST API
 * 3. Static public data file (/data/projects.json)
 * 4. Bundled seed data
 */
export async function getProjects(): Promise<{ data: Project[]; isLive: boolean }> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PROJECTS);
    if (stored) {
      return { data: JSON.parse(stored) as Project[], isLive: false };
    }
  } catch {}

  const url = `${API_BASE_URL}/projects?order=pinned.desc,created_at.desc`;
  const remote = await safeFetch<Project[]>(url);

  if (remote && Array.isArray(remote) && remote.length > 0) {
    return { data: remote, isLive: true };
  }

  // Fallback to static public file if available
  const local = await safeFetch<Project[]>('/data/projects.json');
  if (local && Array.isArray(local) && local.length > 0) {
    return { data: local, isLive: false };
  }

  // Fallback to bundled seed data
  return { data: seedProjects, isLive: false };
}

/**
 * Fetch articles:
 * 1. Client localStorage override (configured via /admin)
 * 2. Remote PostgREST API
 * 3. Static public data file (/data/blogs.json)
 * 4. Bundled seed data
 */
export async function getArticles(): Promise<{ data: Article[]; isLive: boolean }> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.BLOGS);
    if (stored) {
      return { data: JSON.parse(stored) as Article[], isLive: false };
    }
  } catch {}

  const url = `${API_BASE_URL}/articles?order=created_at.desc`;
  const remote = await safeFetch<Article[]>(url);

  if (remote && Array.isArray(remote) && remote.length > 0) {
    return { data: remote, isLive: true };
  }

  const local = await safeFetch<Article[]>('/data/blogs.json');
  if (local && Array.isArray(local) && local.length > 0) {
    return { data: local, isLive: false };
  }

  return { data: seedArticles, isLive: false };
}

/**
 * Fetch single article by slug.
 */
export async function getArticleBySlug(slug: string): Promise<Article | null> {
  const { data: all } = await getArticles();
  const match = all.find((a) => a.slug === slug);
  if (match) return match;

  const url = `${API_BASE_URL}/articles?slug=eq.${encodeURIComponent(slug)}&limit=1`;
  const remote = await safeFetch<Article[]>(url);
  if (remote && Array.isArray(remote) && remote.length > 0) {
    return remote[0];
  }

  return null;
}

/**
 * Fetch social connect channels:
 * 1. Client localStorage override (configured via /admin)
 * 2. Static public data file (/data/social.json)
 * 3. Bundled seed data
 */
export async function getSocialChannels(): Promise<{ data: SocialChannel[]; isLive: boolean }> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SOCIAL);
    if (stored) {
      return { data: JSON.parse(stored) as SocialChannel[], isLive: false };
    }
  } catch {}

  const local = await safeFetch<SocialChannel[]>('/data/social.json');
  if (local && Array.isArray(local) && local.length > 0) {
    return { data: local, isLive: false };
  }

  return { data: seedSocialChannels, isLive: false };
}

/**
 * Fetch monthly telemetry metrics:
 * 1. Client localStorage override (configured via /admin)
 * 2. Remote PostgREST API
 * 3. Static public data file (/data/telemetry.json)
 * 4. Bundled seed data
 */
export async function getLifeMetrics(): Promise<{ data: LifeMetric[]; isLive: boolean }> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TELEMETRY);
    if (stored) {
      return { data: JSON.parse(stored) as LifeMetric[], isLive: false };
    }
  } catch {}

  const url = `${API_BASE_URL}/life_metrics?order=month.desc`;
  const remote = await safeFetch<LifeMetric[]>(url);

  if (remote && Array.isArray(remote) && remote.length > 0) {
    return { data: remote, isLive: true };
  }

  const local = await safeFetch<LifeMetric[]>('/data/telemetry.json');
  if (local && Array.isArray(local) && local.length > 0) {
    return { data: local, isLive: false };
  }

  return { data: seedLifeMetrics, isLive: false };
}

/**
 * Fetch about & identity data:
 * 1. Client localStorage override
 * 2. Static public data file (/data/about.json)
 * 3. Bundled seed data
 */
export async function getAboutData(): Promise<AboutData> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ABOUT);
    if (stored) {
      return JSON.parse(stored) as AboutData;
    }
  } catch {}

  const local = await safeFetch<AboutData>('/data/about.json');
  if (local && typeof local === 'object') {
    return local;
  }

  return seedAboutData;
}

/**
 * Fetch site controls (visibility toggles, location label).
 */
export async function getSiteControls(): Promise<SiteControls> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONTROLS);
    if (stored) {
      return { ...defaultSiteControls, ...JSON.parse(stored) };
    }
  } catch {}

  const url = `${API_BASE_URL}/site_controls`;
  const remote = await safeFetch<SiteControlRow[]>(url);

  if (remote && Array.isArray(remote)) {
    const controls = { ...defaultSiteControls };
    for (const row of remote) {
      if (row.key === 'show_location') controls.show_location = Boolean(row.value);
      if (row.key === 'location_label' && typeof row.value === 'string') controls.location_label = row.value;
      if (row.key === 'show_life_pulse') controls.show_life_pulse = Boolean(row.value);
      if (row.key === 'show_visitor_count') controls.show_visitor_count = Boolean(row.value);
    }
    return controls;
  }

  return defaultSiteControls;
}

/**
 * Save projects override in localStorage and notify listeners.
 */
export function saveProjectsOverride(projects: Project[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    notifyContentChange();
  } catch {}
}

/**
 * Save articles override in localStorage and notify listeners.
 */
export function saveArticlesOverride(articles: Article[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(articles));
    notifyContentChange();
  } catch {}
}

/**
 * Save social channels override in localStorage and notify listeners.
 */
export function saveSocialOverride(channels: SocialChannel[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.SOCIAL, JSON.stringify(channels));
    notifyContentChange();
  } catch {}
}

/**
 * Save life metrics override in localStorage and notify listeners.
 */
export function saveLifeMetricsOverride(metrics: LifeMetric[]) {
  try {
    localStorage.setItem(STORAGE_KEYS.TELEMETRY, JSON.stringify(metrics));
    notifyContentChange();
  } catch {}
}

/**
 * Save about data override in localStorage and notify listeners.
 */
export function saveAboutOverride(about: AboutData) {
  try {
    localStorage.setItem(STORAGE_KEYS.ABOUT, JSON.stringify(about));
    notifyContentChange();
  } catch {}
}

/**
 * Reset all local overrides back to original data files.
 */
export function resetAllContentOverrides() {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROJECTS);
    localStorage.removeItem(STORAGE_KEYS.BLOGS);
    localStorage.removeItem(STORAGE_KEYS.SOCIAL);
    localStorage.removeItem(STORAGE_KEYS.TELEMETRY);
    localStorage.removeItem(STORAGE_KEYS.ABOUT);
    localStorage.removeItem(STORAGE_KEYS.CONTROLS);
    notifyContentChange();
  } catch {}
}

/**
 * Record a unique visitor per browser session via PostgREST RPC.
 * Will only invoke once per tab session to avoid inflated counts.
 */
export async function trackVisitorSession(): Promise<number | null> {
  const SESSION_KEY = 'shvms_session_counted';
  const hasCounted = sessionStorage.getItem(SESSION_KEY);

  if (hasCounted) {
    const res = await safeFetch<{ count: number }[]>(`${API_BASE_URL}/site_metrics?metric_name=eq.visitors`);
    return res && res[0] ? res[0].count : null;
  }

  const res = await safeFetch<number>(`${API_BASE_URL}/rpc/increment_visitor`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (typeof res === 'number') {
    sessionStorage.setItem(SESSION_KEY, '1');
    return res;
  }

  return null;
}

/**
 * Submit changes from Admin panel to backend auth proxy if configured.
 */
export async function sendAdminUpdate(
  endpoint: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  payload: unknown,
  token?: string
): Promise<{ success: boolean; error?: string }> {
  if (!ADMIN_API_URL) {
    return {
      success: true,
      error: 'Admin auth proxy not configured. Changes saved to client session.',
    };
  }

  try {
    const res = await fetch(`${ADMIN_API_URL}/${endpoint.replace(/^\//, '')}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      return { success: false, error: `HTTP ${res.status}: ${res.statusText}` };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Network error' };
  }
}
